import * as React from 'react';
import { CaretDown, SlidersHorizontal } from '@phosphor-icons/react';
import { Popover } from '@fragments-sdk/ui';
import {
  DECISION_ACTIONABILITY_OPTIONS,
  DECISION_KIND_OPTIONS,
  DECISION_POLICY_STATUS_OPTIONS,
  type FindingsDecisionActionability,
  type FindingsDecisionKind,
  type FindingsDecisionPolicyStatus,
  type FindingsGroupBy,
  type FindingsSearch,
  type FindingsSeverity,
  type FindingsView,
} from '../findings-url';
import {
  formatCompactCount,
  formatCategoryLabel,
} from '../findings-display-model';
import { useFindingsData } from '../use-findings-data';
import { formatRuleLabel } from '@/lib/govern-rule-catalog';
import s from '../findings.module.scss';

export const VIEW_FILTERS: { label: string; value: FindingsView }[] = [
  { label: 'All', value: 'all' },
  { label: 'CI / CD', value: 'ci' },
  { label: 'Repo Scan', value: 'repo-scan' },
];

export const DECISION_ACTIONABILITY_LABELS: Record<
  FindingsDecisionActionability,
  string
> = {
  safe_fix: 'Safe fixes',
  guided_fix: 'Guided fixes',
  policy_needed: 'Policy needed',
  manual_review: 'Manual review',
  blocked: 'Blocked',
};

export const DECISION_POLICY_STATUS_LABELS: Record<
  FindingsDecisionPolicyStatus,
  string
> = {
  declared: 'Declared',
  inferred: 'Inferred',
  generic_default: 'Generic default',
  missing: 'Missing',
  not_required: 'Not required',
};

export const DECISION_KIND_LABELS: Record<FindingsDecisionKind, string> = {
  token_decision: 'Token decision',
  token_raw_value: 'Raw token value',
  token_duplicate_value: 'Duplicate token value',
  token_off_scale_value: 'Off-scale token value',
  component_adoption: 'Component adoption',
  component_api_repair: 'Component API repair',
  component_consolidation: 'Component consolidation',
  component_policy_violation: 'Component policy',
  a11y_regression: 'Accessibility',
  policy_readiness: 'Policy readiness',
  tailwind_token_decision: 'Tailwind token',
  tailwind_raw_color_token: 'Tailwind raw color token',
  tailwind_unknown_palette_token: 'Tailwind palette token',
  tailwind_arbitrary_color: 'Tailwind arbitrary color',
  tailwind_arbitrary_spacing: 'Tailwind arbitrary spacing',
  tailwind_off_scale_spacing: 'Tailwind off-scale spacing',
  css_raw_value: 'CSS raw value',
  css_off_scale_spacing: 'CSS off-scale spacing',
  other: 'Other',
};

const SEVERITY_FILTERS: { label: string; value: FindingsSeverity }[] = [
  { label: 'Critical', value: 'critical' },
  { label: 'Serious', value: 'serious' },
  { label: 'Moderate', value: 'moderate' },
  { label: 'Minor', value: 'minor' },
];

export type ActiveFilterChip = {
  key: string;
  label: string;
  count?: number;
  leading?: React.ReactNode;
  clear: Partial<FindingsSearch>;
};

export function FindingsActiveFilters({
  filters,
  onClear,
}: {
  filters: ActiveFilterChip[];
  onClear: (patch: Partial<FindingsSearch>) => void;
}) {
  if (filters.length === 0) return null;
  return (
    <>
      {filters.map((filter) => (
        <FilterOptionChip
          key={filter.key}
          label={filter.label}
          count={filter.count}
          leading={filter.leading}
          active
          onClick={() => onClear(filter.clear)}
        />
      ))}
    </>
  );
}

function FilterOptionChip({
  label,
  count,
  active,
  leading,
  onClick,
}: {
  label: string;
  count?: number;
  active: boolean;
  leading?: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={s.filterOptionChip}
      data-active={active || undefined}
      onClick={onClick}
      title={active ? `Clear ${label} filter` : `Filter by ${label}`}
    >
      {leading}
      <span className={s.filterOptionLabel}>{label}</span>
      {typeof count === 'number' ? (
        <span className={s.filterOptionCount}>{count}</span>
      ) : null}
    </button>
  );
}

function SidebarFilterRow({
  label,
  count,
  active,
  disabled,
  leading,
  onClick,
}: {
  label: string;
  count?: number;
  active: boolean;
  disabled?: boolean;
  leading?: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={s.filterRow}
      data-active={active || undefined}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
    >
      <span className={s.filterRowIndicator} aria-hidden="true" />
      <span className={s.filterRowLeading}>{leading}</span>
      <span className={s.filterRowLabel}>{label}</span>
      {typeof count === 'number' ? (
        <span className={s.filterRowCount}>{count}</span>
      ) : null}
    </button>
  );
}

function SidebarFilterGroup({
  label,
  count,
  active,
  scroll,
  children,
}: {
  label: string;
  count?: number;
  active?: boolean;
  scroll?: boolean;
  children: React.ReactNode;
}) {
  return (
    <details className={s.filterGroup} data-active={active || undefined} open>
      <summary className={s.filterGroupSummary}>
        <CaretDown size={11} className={s.filterGroupCaret} />
        <span className={s.filterGroupLabel}>{label}</span>
        {typeof count === 'number' ? (
          <span className={s.filterGroupCount}>{count}</span>
        ) : null}
      </summary>
      <div className={s.filterGroupRows} data-scroll={scroll || undefined}>
        {children}
      </div>
    </details>
  );
}

export function WorkbenchSegment<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<{ label: string; value: T; count?: number }>;
  onChange: (value: T) => void;
}) {
  return (
    <div className={s.workbenchSegment} aria-label={label}>
      <span className={s.workbenchSegmentLabel}>{label}</span>
      <div className={s.segmentedControl}>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            className={s.segmentedButton}
            data-active={value === option.value || undefined}
            onClick={() => onChange(option.value)}
          >
            <span>{option.label}</span>
            {typeof option.count === 'number' && (
              <strong>{formatCompactCount(option.count)}</strong>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

export function FindingsFilterPopover({
  activeView,
  activeSeverity,
  activeRule,
  activeCategory,
  activeArea,
  activeDecisionActionability,
  activeDecisionPolicyStatus,
  activeDecisionKind,
  groupBy,
  counts,
  activeCount,
  onUpdate,
  onClear,
}: {
  activeView: FindingsView;
  activeSeverity: FindingsSeverity | null;
  activeRule: string | null;
  activeCategory: string | null;
  activeArea: string | null;
  activeDecisionActionability: FindingsDecisionActionability | null;
  activeDecisionPolicyStatus: FindingsDecisionPolicyStatus | null;
  activeDecisionKind: FindingsDecisionKind | null;
  groupBy: FindingsGroupBy;
  counts: NonNullable<ReturnType<typeof useFindingsData>['counts']> | undefined;
  activeCount: number;
  onUpdate: (patch: Partial<FindingsSearch>) => void;
  onClear: () => void;
}) {
  const decisionFiltersEnabled = groupBy === 'decision';
  const areaEntries = counts?.byArea
    ? Object.entries(counts.byArea).sort(([, a], [, b]) => b - a)
    : [];
  const areaLabel = (areaId: string) =>
    areaId === 'unassigned'
      ? 'Unassigned'
      : (counts?.areaNames?.[areaId] ?? areaId);

  return (
    <Popover>
      <Popover.Trigger className={s.filtersTrigger}>
        <SlidersHorizontal size={13} />
        Filters
        {activeCount > 0 && (
          <span className={s.filtersTriggerCount}>{activeCount}</span>
        )}
      </Popover.Trigger>
      <Popover.Content
        size="md"
        align="end"
        side="bottom"
        sideOffset={8}
        className={s.filtersPopover}
      >
        <Popover.Title className={s.filtersPopoverTitle}>
          <span>Filters</span>
          {activeCount > 0 && (
            <button
              type="button"
              className={s.filterClearAll}
              onClick={onClear}
            >
              Clear all
            </button>
          )}
        </Popover.Title>
        <Popover.Body className={s.filtersPopoverBody}>
          <SidebarFilterGroup label="Source" active={activeView !== 'all'}>
            {VIEW_FILTERS.map((view) => (
              <SidebarFilterRow
                key={view.value}
                label={view.label}
                active={activeView === view.value}
                onClick={() =>
                  onUpdate({
                    view: view.value === 'all' ? undefined : view.value,
                  })
                }
              />
            ))}
          </SidebarFilterGroup>

          <div
            className={s.decisionFilterBlock}
            data-disabled={!decisionFiltersEnabled || undefined}
          >
            <div className={s.decisionFilterNote}>
              Decision filters apply when grouping by Decision.
            </div>
            <SidebarFilterGroup
              label="Actionability"
              active={Boolean(activeDecisionActionability)}
            >
              {DECISION_ACTIONABILITY_OPTIONS.map((value) => (
                <SidebarFilterRow
                  key={value}
                  label={DECISION_ACTIONABILITY_LABELS[value]}
                  active={activeDecisionActionability === value}
                  disabled={!decisionFiltersEnabled}
                  onClick={() => {
                    if (!decisionFiltersEnabled) return;
                    onUpdate({
                      actionability:
                        activeDecisionActionability === value
                          ? undefined
                          : value,
                    });
                  }}
                />
              ))}
            </SidebarFilterGroup>

            <SidebarFilterGroup
              label="Policy status"
              active={Boolean(activeDecisionPolicyStatus)}
            >
              {DECISION_POLICY_STATUS_OPTIONS.map((value) => (
                <SidebarFilterRow
                  key={value}
                  label={DECISION_POLICY_STATUS_LABELS[value]}
                  active={activeDecisionPolicyStatus === value}
                  disabled={!decisionFiltersEnabled}
                  onClick={() => {
                    if (!decisionFiltersEnabled) return;
                    onUpdate({
                      policyStatus:
                        activeDecisionPolicyStatus === value
                          ? undefined
                          : value,
                    });
                  }}
                />
              ))}
            </SidebarFilterGroup>

            <SidebarFilterGroup
              label="Decision kind"
              active={Boolean(activeDecisionKind)}
              scroll
            >
              {DECISION_KIND_OPTIONS.map((value) => (
                <SidebarFilterRow
                  key={value}
                  label={DECISION_KIND_LABELS[value]}
                  active={activeDecisionKind === value}
                  disabled={!decisionFiltersEnabled}
                  onClick={() => {
                    if (!decisionFiltersEnabled) return;
                    onUpdate({
                      decisionKind:
                        activeDecisionKind === value ? undefined : value,
                    });
                  }}
                />
              ))}
            </SidebarFilterGroup>
          </div>

          {counts &&
            counts.bySeverity &&
            Object.keys(counts.bySeverity).length > 0 && (
              <SidebarFilterGroup
                label="Severity"
                active={Boolean(activeSeverity)}
              >
                {SEVERITY_FILTERS.map(({ value: sev, label }) => {
                  const count =
                    (counts.bySeverity as Record<string, number>)[sev] ?? 0;
                  if (count === 0) return null;
                  return (
                    <SidebarFilterRow
                      key={sev}
                      label={label}
                      count={count}
                      active={activeSeverity === sev}
                      leading={
                        <span className={s.severityDot} data-severity={sev} />
                      }
                      onClick={() =>
                        onUpdate({
                          severity: activeSeverity === sev ? undefined : sev,
                        })
                      }
                    />
                  );
                })}
              </SidebarFilterGroup>
            )}

          {counts && Object.keys(counts.byRule).length > 0 && (
            <SidebarFilterGroup
              label="Rule"
              active={Boolean(activeRule)}
              scroll
            >
              {Object.entries(counts.byRule).map(([ruleId, count]) => (
                <SidebarFilterRow
                  key={ruleId}
                  label={formatRuleLabel(ruleId)}
                  count={count}
                  active={activeRule === ruleId}
                  onClick={() =>
                    onUpdate({
                      rule: activeRule === ruleId ? undefined : ruleId,
                    })
                  }
                />
              ))}
            </SidebarFilterGroup>
          )}

          {counts && Object.keys(counts.byCategory).length > 0 && (
            <SidebarFilterGroup
              label="Category"
              active={Boolean(activeCategory)}
              scroll
            >
              {Object.entries(counts.byCategory)
                .sort(([, a], [, b]) => b - a)
                .map(([cat, count]) => (
                  <SidebarFilterRow
                    key={cat}
                    label={formatCategoryLabel(cat)}
                    count={count}
                    active={activeCategory === cat}
                    leading={
                      <span className={s.categoryDot} data-category={cat} />
                    }
                    onClick={() =>
                      onUpdate({
                        category: activeCategory === cat ? undefined : cat,
                      })
                    }
                  />
                ))}
            </SidebarFilterGroup>
          )}

          {areaEntries.length > 0 && (
            <SidebarFilterGroup
              label="Area"
              active={Boolean(activeArea)}
              scroll
            >
              {areaEntries.map(([areaId, count]) => (
                <SidebarFilterRow
                  key={areaId}
                  label={areaLabel(areaId)}
                  count={count}
                  active={activeArea === areaId}
                  onClick={() =>
                    onUpdate({
                      area: activeArea === areaId ? undefined : areaId,
                    })
                  }
                />
              ))}
            </SidebarFilterGroup>
          )}
        </Popover.Body>
      </Popover.Content>
    </Popover>
  );
}
